import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {patchJSONPromise, postJSONPromise} from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

const API_BASE = getApiBaseForObjectType(DomainGraphType.SCRATCH_PLATE);

// -- Shared nested types ---------------------------------------------------

export interface UnitInput {
  apiId: string;
  versionNumber?: number | null;
}

export interface QuantityInput {
  amount: number;
  unit?: UnitInput | null;
}

export interface ScratchPlateWellInput {
  rowIndex: number;
  columnIndex: number;
  role?: string | null;
  subrole?: string | null;
  group?: string | null;
  sampleSchemaId?: string | null;
  schemaTags?: Record<string, unknown> | null;
}

export interface ConcentrationWellInput {
  wellPosition: string;
  concentrationAmount?: number | null;
}

export interface ConcentrationLayerInput {
  name: string;
  concentrationUnitApiId: string;
  concentrationUnitVersionNumber: number;
  wells?: Array<ConcentrationWellInput>;
}

export interface ScratchPlateMeasurementInput {
  rowIndex: number;
  columnIndex: number;
  measurementIdentifier?: string | null;
  measurementName?: string | null;
  measurementConcept?: string | null;
  measurementValue?: number | null;
  measurementUnit?: string | null;
  detectionType?: string | null;
  analyticalMethod?: string | null;
}

export interface ScratchPlateVendorFileInput {
  scratchFileId: string;
  vendor: string;
}

export interface ScratchPlatePlateReaderAsmInput {
  scratchFileId: string;
}

// A well placement specified by an entity, or by a container (resolved server-side to the entity it
// holds at create time). Exactly one of entityApiId / containerApiId is provided.
type ContentItemIdentifier =
  | {entityApiId: string; containerApiId?: null}
  | {containerApiId: string; entityApiId?: null};

export type ContentItemInput = ContentItemIdentifier & {
  wellPosition: string;
  rowIndex: number;
  columnIndex: number;
  contentLayerName: string | null;
  concentrationAmount: number | null;
  concentrationUnitApiId: string | null;
  concentrationUnitVersionNumber: number | null;
  transferQuantityAmount: number | null;
  transferQuantityUnitApiId: string | null;
  transferQuantityUnitVersionNumber: number | null;
};

export interface MetadataWellInput {
  wellPosition: string;
  value: string | number | boolean | null;
}

export type MetadataLayerApiType = 'text' | 'integer' | 'float' | 'boolean' | 'date' | 'datetime';

export interface MetadataColumnInput {
  name: string;
  type: MetadataLayerApiType;
  wells: Array<MetadataWellInput>;
}

// -- CRUD types ------------------------------------------------------------

interface CreateScratchPlateBaseParams {
  authParentApiId: string;
  originDesignApiId?: string | null;
  name: string;
  height: number;
  width: number;
  plateSchemaId?: string | null;
  plateMetadata?: Record<string, string | number>;
  wells?: Array<ScratchPlateWellInput>;
  concentrationLayers?: Array<ConcentrationLayerInput>;
  metadataLayers?: Array<MetadataColumnInput>;
  measurements?: Array<ScratchPlateMeasurementInput>;
  contents?: Array<Array<ContentItemInput>>;
}

export type CreateScratchPlateParams = CreateScratchPlateBaseParams &
  (
    | {vendorFile?: null; plateReaderAsm?: null}
    | {vendorFile: ScratchPlateVendorFileInput; plateReaderAsm?: null}
    | {vendorFile?: null; plateReaderAsm: ScratchPlatePlateReaderAsmInput}
  );

export interface CreateScratchPlatesResponse {
  createdIds: Array<string>;
}

export interface UpdateScratchPlateParams {
  name?: string;
  height?: number;
  width?: number;
  plateSchemaId?: string | null;
  plateMetadata?: Record<string, string | number>;
  wells?: Array<ScratchPlateWellInput>;
  concentrationLayers?: Array<ConcentrationLayerInput>;
  metadataLayers?: Array<MetadataColumnInput>;
}

export interface UpdateScratchPlatesResponse {
  updatedIds: Array<string>;
}

export interface ScratchPlateResponse {
  id: string;
  name: string;
  height: number;
  width: number;
  createdAt: string;
  modifiedAt: string;
}

export interface ListScratchPlatesParams {
  pageSize?: number;
  nextToken?: string;
}

export interface ListScratchPlatesResponse {
  items: Array<ScratchPlateResponse>;
  nextToken?: string;
}

export enum GridDirection {
  ACROSS_ROWS = 'ACROSS_ROWS',
  DOWN_COLUMNS = 'DOWN_COLUMNS',
}

// -- Transfer types --------------------------------------------------------

export interface WellTransferInput {
  sourcePlateId: string;
  sourceWellPosition: string;
  destinationPlateId: string;
  destinationWellPosition: string;
  transferQuantity?: QuantityInput | null;
}

export enum PlateLayoutType {
  STAMP = 'STAMP',
  STAMP_TO_QUADRANTS = 'STAMP_TO_QUADRANTS',
  REVERSE_STAMP_TO_QUADRANTS = 'REVERSE_STAMP_TO_QUADRANTS',
  INTERLEAVE = 'INTERLEAVE',
  REVERSE_INTERLEAVE = 'REVERSE_INTERLEAVE',
  COLUMN_INTERLEAVE = 'COLUMN_INTERLEAVE',
  REVERSE_COLUMN_INTERLEAVE = 'REVERSE_COLUMN_INTERLEAVE',
}

export interface TransferScratchPlateParams {
  operations?: Array<WellTransferInput>;
  sourcePlateId?: string | null;
  destinationPlateIds?: Array<string>;
  layout?: PlateLayoutType | null;
  direction?: GridDirection;
  transferQuantity?: QuantityInput | null;
  skipEmpty?: boolean;
}

// -- DAO functions ---------------------------------------------------------

export function createScratchPlates(
  creates: Array<CreateScratchPlateParams>
): Promise<CreateScratchPlatesResponse> {
  return postJSONPromise({url: API_BASE, data: {creates}});
}

export function updateScratchPlates(
  updates: Array<{id: string} & UpdateScratchPlateParams>
): Promise<UpdateScratchPlatesResponse> {
  return patchJSONPromise({url: API_BASE, data: {updates}});
}

export function bulkTransferScratchPlates(
  items: Array<TransferScratchPlateParams>
): Promise<TaskRunner.OngoingTaskResponse> {
  return TaskRunner.postJSONPromise({
    url: `${API_BASE}:bulk-transfer`,
    data: {items},
  });
}

// -- Put in Inventory types ---------------------------------------------------

/**
 * Maps one scratch-plate layer column onto a container-schema field. Source
 * columns are qualified by `layerName` because the same column name can appear
 * in more than one layer; `schemaFieldName` is the target field's system name.
 */
export interface ScratchPlateColumnMapping {
  layerName: string;
  columnName: string;
  schemaFieldName: string;
}

export interface PutScratchPlateInInventoryParams {
  scratchPlateId: string;
  plateSchemaId?: string | null;
  name?: string | null;
  barcodeValue?: string | null;
  sourceApiId?: string | null;
  parentStorableApiId?: string | null;
  columnMappings?: Array<ScratchPlateColumnMapping> | null;
  studyApiIds?: Array<string>;
}

export interface PutScratchPlateInInventoryResponse {
  plate: {id: string; name: string};
}

// -- Put in Inventory DAO function --------------------------------------------

export function putScratchPlateInInventory(
  params: PutScratchPlateInInventoryParams
): Promise<PutScratchPlateInInventoryResponse> {
  return postJSONPromise({
    url: `${API_BASE}:put-in-inventory`,
    data: {
      scratchPlateId: params.scratchPlateId,
      plateSchemaId: params.plateSchemaId ?? undefined,
      name: params.name ?? undefined,
      barcodeValue: params.barcodeValue ?? undefined,
      sourceApiId: params.sourceApiId ?? undefined,
      parentStorableApiId: params.parentStorableApiId ?? undefined,
      columnMappings: params.columnMappings?.length ? params.columnMappings : undefined,
      studyApiIds: params.studyApiIds ?? undefined,
    },
  });
}

// -- List mappable columns ----------------------------------------------------

export interface MappableColumn {
  systemName: string;
  displayName: string;
  leafType: string | null;
}

export interface MappableLayer {
  layerName: string;
  columns: Array<MappableColumn>;
}

export interface ListMappableColumnsResponse {
  layers: Array<MappableLayer>;
}

/** Fetch the columns each layer of a scratch plate exposes for column mapping. */
export function listScratchPlateMappableColumns(
  scratchPlateId: string
): Promise<ListMappableColumnsResponse> {
  return postJSONPromise({
    url: `${API_BASE}:list-mappable-columns`,
    data: {scratchPlateId},
  });
}
